import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { GetAllProductsResult } from '@mews-distributor/data-access';
import { keyById } from '@mews/fp';
import { ProductsSlice } from './types';

const dataInitialState: ProductsSlice['data'] = {};
const metaInitialState: ProductsSlice['meta'] = {};

interface FetchProductsSucceededPayload extends GetAllProductsResult {
    serviceId: string;
    enterpriseId: string;
}

const productsDataSlice = createSlice({
    name: 'productsData',
    initialState: dataInitialState,
    reducers: {
        fetchProductsSucceeded: (
            state,
            { payload: { products, enterpriseId } }: PayloadAction<FetchProductsSucceededPayload>
        ) =>
            pipe(
                products,
                array.map((product) => ({ ...product, enterpriseId })),
                keyById,
                (extendedProducts) => ({ ...state, ...extendedProducts })
            ),
    },
});

export const { fetchProductsSucceeded } = productsDataSlice.actions;

interface MetaPayload {
    serviceId: string;
}

const productsMetaSlice = createSlice({
    name: 'productsMeta',
    initialState: metaInitialState,
    reducers: {
        fetchProductsStarted: (state, { payload: { serviceId } }: PayloadAction<MetaPayload>) => {
            state[serviceId] = { hasFailed: false, isFetching: true };
        },
        fetchProductsFailed: (state, { payload: { serviceId } }: PayloadAction<MetaPayload>) => {
            state[serviceId] = { hasFailed: true, isFetching: false };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchProductsSucceeded, (state, { payload: { serviceId } }) => {
            state[serviceId] = { hasFailed: false, isFetching: false };
        });
    },
});

export const productsReducer = combineReducers({
    data: productsDataSlice.reducer,
    meta: productsMetaSlice.reducer,
});

export const { fetchProductsStarted, fetchProductsFailed } = productsMetaSlice.actions;
